import useBuiltInImportSources from "@app/composables/use-built-in-import-sources";
import { generateSearchQuery } from "@app/utils/generateSearchQuery/generateSearchQuery";
import { generatePmsSearchQuery } from "@app/utils/generateSearchQuery/generators/pms";
import { Review } from "@core/domain/models/review.model";
import { BuiltInImportSourceId } from "@core/domain/types/builtInImportSourceId";
import { CochraneApiClient } from "@infrastructure/cochraneApi/cochraneApi.client";
import { DaenApiClient } from "@infrastructure/daenApi/daenApi.client";
import { ReviewsRepository } from "@infrastructure/datasource/reviews.repository";
import { EuropePmcApiClient } from "@infrastructure/europePmcApi/europePmcApi.client";
import { MdidApiClient } from "@infrastructure/mdidApi/mdidApi.client";
import { OpenAlexApiClient } from "@infrastructure/openAlexApi/openAlexApi.client";
import { OpenFdaApiClient } from "@infrastructure/openFdaApi/openFdaApi.client";
import { PmcApiClient } from "@infrastructure/pmcApi/pmcApi.client";
import { ProsperoApiClient } from "@infrastructure/prosperoApi/prosperoApi.client";
import { PubmedApiClient } from "@infrastructure/pubmedApi/pubmedApi.client";
import { getSafetyInformationSources } from "./safetyInformation.service";
import { FsnApiClient } from "@infrastructure/fsnApi/fsnApi.client";
import { ImportSourceType } from "@core/domain/types/import-source-type.type";

export class AutoImportService {
  private readonly builtInImportSources = useBuiltInImportSources();
  constructor(
    private readonly _reviewsRepository: ReviewsRepository,
    private readonly pmcApiClient: PmcApiClient,
    private readonly europePmcApiClient: EuropePmcApiClient,
    private readonly openAlexApiClient: OpenAlexApiClient,
    private readonly pubmedApiClient: PubmedApiClient,
    private readonly fsnApiClient: FsnApiClient,
    private readonly openFdaApiClient: OpenFdaApiClient,
    private readonly mdidApiClient: MdidApiClient,
    private readonly daenApiClient: DaenApiClient,
    private readonly prosperoApiClient: ProsperoApiClient,
    private readonly cochraneApiClient: CochraneApiClient,
  ) {}

  async *handlePmsWithProgress({
    reviewId,
    safetyInformationSourceIds,
  }: {
    reviewId: number;
    safetyInformationSourceIds: string[];
  }) {
    try {
      const review = await this._reviewsRepository.findById(reviewId);
      if (!review?.plan) return;

      yield `Searching in Pubmed for ${generateSearchQuery(BuiltInImportSourceId.PUBMED, review)}`;
      await this.importPubmedSearch(review).catch((e) => {
        console.warn(`Pubmed search failed for review ${review.id}. Skipping Pubmed import.`, e);
      });

      yield `Searching in Open Alex for ${generateSearchQuery(
        BuiltInImportSourceId.OPEN_ALEX,
        review,
      )}`;
      await this.importOpenAlexSearch(review).catch((e) => {
        console.warn(
          `Open Alex search failed for review ${review.id}. Skipping Open Alex import.`,
          e,
        );
      });

      yield `Searching in Europe PMC for ${generateSearchQuery(
        BuiltInImportSourceId.EUROPE_PMC,
        review,
      )}`;
      await this.importEuropePmcSearch(review).catch((e) => {
        console.warn(
          `Europe PMC search failed for review ${review.id}. Skipping Europe PMC import.`,
          e,
        );
      });

      yield `Searching in PMC for ${generateSearchQuery(BuiltInImportSourceId.PMC, review)}`;
      await this.importPmcSearch(review).catch((e) => {
        console.warn(`PMC search failed for review ${review.id}. Skipping PMC import.`, e);
      });

      yield `Searching in Open fda for ${generateSearchQuery(
        BuiltInImportSourceId.OPEN_FDA,
        review,
      )}`;
      await this.importOpenFdaSearch(review).catch((e) => {
        console.warn(
          `Open FDA search failed for review ${review.id}. Skipping Open FDA import.`,
          e,
        );
      });

      yield `Searching in MDID for ${generateSearchQuery(BuiltInImportSourceId.MDID, review)}`;
      await this.importMdidSearch(review).catch((e) => {
        console.warn(`MDID search failed for review ${review.id}. Skipping MDID import.`, e);
      });

      yield `Searching in DAEN for ${generateSearchQuery(BuiltInImportSourceId.DAEN, review)}`;
      await this.importDaenSearch(review).catch((e) => {
        console.warn(`DAEN search failed for review ${review.id}. Skipping DAEN import.`, e);
      });

      const safetyInformationQuery = generatePmsSearchQuery({
        plan: review.plan,
      });
      yield `Searching in Safety information sources for ${safetyInformationQuery}`;

      for await (const message of this.importSafetyInformationSearches({
        review,
        safetyInformationSourceIds,
      })) {
        yield message;
      }
    } catch (error: any) {
      console.error(`Error handling ReviewCreatedEvent: ${error.message}`, error.stack);
    }
  }

  async *handleMedicalBackgroundWithProgress(reviewId: number) {
    try {
      const review = await this._reviewsRepository.findById(reviewId);
      if (!review?.plan) return;

      yield `Searching in Pubmed for ${generateSearchQuery(BuiltInImportSourceId.PUBMED, review)}`;
      await this.importPubmedSearch(review).catch((e) => {
        console.warn(`Pubmed search failed for review ${review.id}. Skipping Pubmed import.`, e);
      });

      yield `Searching in Europe PMC for ${generateSearchQuery(BuiltInImportSourceId.EUROPE_PMC, review)}`;
      await this.importEuropePmcSearch(review).catch((e) => {
        console.warn(
          `Europe PMC search failed for review ${review.id}. Skipping Europe PMC import.`,
          e,
        );
      });

      yield `Searching in Cochrane for ${generateSearchQuery(
        BuiltInImportSourceId.COCHRANE,
        review,
      )}`;
      await this.importCochraneSearch(review).catch((e) => {
        console.warn(
          `Cochrane search failed for review ${review.id}. Skipping Cochrane import.`,
          e,
        );
      });

      yield `Searching in Prospero for ${generateSearchQuery(
        BuiltInImportSourceId.PROSPERO,
        review,
      )}`;
      await this.importProsperoSearch(review).catch((e) => {
        console.warn(
          `Prospero search failed for review ${review.id}. Skipping Prospero import.`,
          e,
        );
      });
    } catch (error: any) {
      console.error(`Error handling ReviewCreatedEvent: ${error.message}`, error.stack);
    }
  }

  async *handleNotifiedBodyWithProgress(reviewId: number) {
    try {
      const review = await this._reviewsRepository.findById(reviewId);
      if (!review?.plan) return;

      yield `Searching in Pubmed for ${generateSearchQuery(BuiltInImportSourceId.PUBMED, review)}`;
      await this.importPubmedSearch(review).catch((e) => {
        console.warn(`Pubmed search failed for review ${review.id}. Skipping Pubmed import.`, e);
      });
    } catch (error: any) {
      console.error(`Error handling ReviewCreatedEvent: ${error.message}`, error.stack);
    }
  }

  async *handleProcedureWithProgress(reviewId: number) {
    try {
      const review = await this._reviewsRepository.findById(reviewId);
      if (!review?.plan) return;

      yield `Searching in Pubmed for ${generateSearchQuery(BuiltInImportSourceId.PUBMED, review)}`;
      await this.importPubmedSearch(review).catch((e) => {
        console.warn(`Pubmed search failed for review ${review.id}. Skipping Pubmed import.`, e);
      });

      yield `Searching in Europe PMC for ${generateSearchQuery(BuiltInImportSourceId.EUROPE_PMC, review)}`;
      await this.importEuropePmcSearch(review).catch((e) => {
        console.warn(
          `Europe PMC search failed for review ${review.id}. Skipping Europe PMC import.`,
          e,
        );
      });
    } catch (error: any) {
      console.error(`Error handling ReviewCreatedEvent: ${error.message}`, error.stack);
    }
  }

  private async importPubmedSearch(review: Review) {
    const pubmedSearch = await this.pubmedApiClient.createReviewSearch(review);
    if (!this.sourceExistsInPlan(BuiltInImportSourceId.PUBMED, review)) {
      const pubmedSource = this.builtInImportSources.findById(BuiltInImportSourceId.PUBMED);
      await this._reviewsRepository.addImportSourceToPlan(review.id, {
        name: pubmedSource.name,
        type: pubmedSource.type,
        url: pubmedSource.url ?? "",
        description: pubmedSource.description,
      });
    }

    await this._reviewsRepository.importSearch(pubmedSearch);
  }

  private async importOpenAlexSearch(review: Review) {
    const openAlexSearch = await this.openAlexApiClient.createReviewSearch(review);
    if (!this.sourceExistsInPlan(BuiltInImportSourceId.OPEN_ALEX, review)) {
      const openAlexSource = this.builtInImportSources.findById(BuiltInImportSourceId.OPEN_ALEX);
      await this._reviewsRepository.addImportSourceToPlan(review.id, {
        name: openAlexSource.name,
        type: openAlexSource.type,
        url: openAlexSource.url ?? "",
        description: openAlexSource.description,
      });
    }
    await this._reviewsRepository.importSearch(openAlexSearch);
  }

  private async importEuropePmcSearch(review: Review) {
    const europePmcSearch = await this.europePmcApiClient.createReviewSearch(review);
    if (!this.sourceExistsInPlan(BuiltInImportSourceId.EUROPE_PMC, review)) {
      const europePmcSource = this.builtInImportSources.findById(BuiltInImportSourceId.EUROPE_PMC);
      await this._reviewsRepository.addImportSourceToPlan(review.id, {
        name: europePmcSource.name,
        type: europePmcSource.type,
        url: europePmcSource.url ?? "",
        description: europePmcSource.description,
      });
    }
    await this._reviewsRepository.importSearch(europePmcSearch);
  }

  private async importPmcSearch(review: Review) {
    const pmcSearch = await this.pmcApiClient.createReviewSearch(review);
    if (!this.sourceExistsInPlan(BuiltInImportSourceId.PMC, review)) {
      const pmcSource = this.builtInImportSources.findById(BuiltInImportSourceId.PMC);
      await this._reviewsRepository.addImportSourceToPlan(review.id, {
        name: pmcSource.name,
        type: pmcSource.type,
        url: pmcSource.url ?? "",
        description: pmcSource.description,
      });
    }
    await this._reviewsRepository.importSearch(pmcSearch);
  }

  private async importOpenFdaSearch(review: Review) {
    const query = generateSearchQuery(BuiltInImportSourceId.OPEN_FDA, review);
    const openFdaSearch = await this.openFdaApiClient.createReviewSearch(review, query);
    if (!this.sourceExistsInPlan(BuiltInImportSourceId.OPEN_FDA, review)) {
      const openFdaSource = this.builtInImportSources.findById(BuiltInImportSourceId.OPEN_FDA);
      await this._reviewsRepository.addImportSourceToPlan(review.id, {
        name: openFdaSource.name,
        type: openFdaSource.type,
        url: openFdaSource.url ?? "",
        description: openFdaSource.description,
      });
    }
    await this._reviewsRepository.importSearch(openFdaSearch);
  }

  private async importMdidSearch(review: Review) {
    const query = generateSearchQuery(BuiltInImportSourceId.MDID, review);
    const mdidSearch = await this.mdidApiClient.createReviewSearch(review, query);
    if (!this.sourceExistsInPlan(BuiltInImportSourceId.MDID, review)) {
      const mdidSource = this.builtInImportSources.findById(BuiltInImportSourceId.MDID);
      await this._reviewsRepository.addImportSourceToPlan(review.id, {
        name: mdidSource.name,
        type: mdidSource.type,
        url: mdidSource.url ?? "",
        description: mdidSource.description,
      });
    }
    await this._reviewsRepository.importSearch(mdidSearch);
  }

  private async importDaenSearch(review: Review) {
    const query = generateSearchQuery(BuiltInImportSourceId.DAEN, review);
    const daenSearch = await this.daenApiClient.createReviewSearch(review, query);
    if (!this.sourceExistsInPlan(BuiltInImportSourceId.DAEN, review)) {
      const daenSource = this.builtInImportSources.findById(BuiltInImportSourceId.DAEN);
      await this._reviewsRepository.addImportSourceToPlan(review.id, {
        name: daenSource.name,
        type: daenSource.type,
        url: daenSource.url ?? "",
        description: daenSource.description,
      });
    }
    await this._reviewsRepository.importSearch(daenSearch);
  }

  private async importCochraneSearch(review: Review) {
    const cochraneSearch = await this.cochraneApiClient.createReviewSearch(review);
    if (!this.sourceExistsInPlan(BuiltInImportSourceId.COCHRANE, review)) {
      const cochraneSource = this.builtInImportSources.findById(BuiltInImportSourceId.COCHRANE);
      await this._reviewsRepository.addImportSourceToPlan(review.id, {
        name: cochraneSource.name,
        type: cochraneSource.type,
        url: cochraneSource.url ?? "",
        description: cochraneSource.description,
      });
    }
    await this._reviewsRepository.importSearch(cochraneSearch);
  }

  private async importProsperoSearch(review: Review) {
    const prosperoSearch = await this.prosperoApiClient.createReviewSearch(review);
    if (!this.sourceExistsInPlan(BuiltInImportSourceId.PROSPERO, review)) {
      const prosperoSource = this.builtInImportSources.findById(BuiltInImportSourceId.PROSPERO);
      await this._reviewsRepository.addImportSourceToPlan(review.id, {
        name: prosperoSource.name,
        type: prosperoSource.type,
        url: prosperoSource.url ?? "",
        description: prosperoSource.description,
      });
    }
    await this._reviewsRepository.importSearch(prosperoSearch);
  }

  private async *importSafetyInformationSearches({
    review,
    safetyInformationSourceIds,
  }: {
    review: Review;
    safetyInformationSourceIds: string[];
  }) {
    const safetyInformationSearches = await this.fsnApiClient.createReviewSearches({
      review,
      safetyInformationSourceIds,
    });
    const safetyInformationSources = await getSafetyInformationSources();
    for (const search of safetyInformationSearches) {
      yield `Searching in ${search.importSourceId} for ${search.query}`;
      if (!this.sourceExistsInPlan(search.importSourceId, review)) {
        const source = safetyInformationSources.find((s) => s.id === search.importSourceId);
        if (source) {
          await this._reviewsRepository.addImportSourceToPlan(review.id, {
            name: source.name,
            type: ImportSourceType.FIELD_SAFETY_NOTICES,
            url: source.url,
            id: source.id,
            description: source.description,
          });
        }
      }

      await this._reviewsRepository.importSearch(search);
    }
  }

  private sourceExistsInPlan(importSourceId: string, review: Review): boolean {
    const enabledSources = review.plan?.importPlan.importSources || [];
    return enabledSources.some((source) => source.id === importSourceId);
  }
}
